import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

export async function exportExcelInvestigate(data = []) {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("ข้อมูลลูกหนี้");

  const LOCKED_COLS = new Set([14, 15, 16, 17, 18, 19, 20, 21]);

  ws.columns = [
    { header: "ลำดับ", key: "no", width: 8 },
    { header: "เลขสัญญา", key: "contno", width: 18 },
    { header: "รายชื่อ", key: "name", width: 24 },
    { header: "รหัสบัตร", key: "cusCode", width: 25 },
    { header: "ที่อยู่", key: "addr", width: 40 },
    { header: "ไม่พบ", key: "unFound", width: 12 },
    { header: "พบ", key: "found", width: 12 },
    { header: "หมายเหตุ", key: "remark", width: 45 },
    { header: "คัด ทร.14", key: "grovermentCheckAmount", width: 16 },
    { header: "ทะเบียนครอบครัว", key: "familyCheckAmount", width: 16 },
    { header: "ดำเนินงาน", key: "operateAmount", width: 14 },
    { header: "ตรวจเพิ่ม", key: "reCheckAmount", width: 14 },
    { header: "รวม", key: "totalAmount", width: 12 },
    { header: "customer_id", key: "cusId", width: 10 },
    { header: "asset_online_id", key: "assetOnlineId", width: 10 },
    { header: "inv_id", key: "invId", width: 10 },
    { header: "loanId", key: "loanId", width: 10 },
    { header: "roundId", key: "roundId", width: 10 },
    { header: "SCHEMA", key: "SCHEMA", width: 10 },
    { header: "garno", key: "garno", width: 10 },
    { header: "count", key: "count", width: 10 },
  ];

  // ✅ 1. เพิ่มการกำหนด Protection ระดับ Column
  // (เพื่อให้เวลา User กด Insert Row แถวใหม่จะสืบทอด Lock/Unlock จากคอลัมน์อัตโนมัติ)
  ws.columns.forEach((col, index) => {
    const colNumber = index + 1;
    col.protection = { locked: LOCKED_COLS.has(colNumber) };
  });

  // format ที่อยู่ 1 อัน → "ADDR1 ต.TUMB อ.AUMPDES จ.PROVDES ZIP"
  const formatAddress = (a) =>
    [
      a?.ADDR1,
      a?.TUMB && `ต.${a.TUMB}`,
      a?.AUMPDES && `อ.${a.AUMPDES}`,
      a?.PROVDES && `จ.${a.PROVDES}`,
      a?.ZIP,
    ]
      .filter(Boolean)
      .join(" ");

  // รวมทุก address ใน array (มีหลายอันขึ้นบรรทัดใหม่)
  const formatAddressList = (addresses = []) =>
    addresses
      .map((a, i) =>
        addresses.length > 1
          ? `${i + 1}. ${formatAddress(a)}`
          : formatAddress(a),
      )
      .join("\n");

  // ── จัดกลุ่มตาม contno ──────────────────────────────────────
  const grouped = data.reduce((m, row) => {
    const key = String(row?.contno || "").trim();
    if (!key) return m;
    if (!m.has(key)) m.set(key, []);
    m.get(key).push(row);
    return m;
  }, new Map());

  // ── เพิ่ม data rows ──────────────────────────────────────────
  Array.from(grouped.entries()).forEach(([contno, rows], groupIndex) => {
    rows.forEach((r, idxInGroup) => {
      const fullName = [r.SNAM, r.NAME1, r.NAME2]
        .filter(Boolean)
        .join(" ")
        .trim();

      const excelRow = ws.addRow({
        no: idxInGroup === 0 ? groupIndex + 1 : "",
        contno: idxInGroup === 0 ? contno : "",
        name: fullName || "",
        cusCode: r.CUSCOD || "",
        addr: formatAddressList(r.ADDRESS), // ✅ ต่อทุก address
        unFound: "",
        found: "",
        remark: "",
        grovermentCheckAmount: "",
        familyCheckAmount: "",
        operateAmount: "",
        reCheckAmount: "",
        totalAmount: "",
        cusId: r.customerId ?? "",
        assetOnlineId: r.assetOnlineId ?? "",
        invId: r.invId ?? "",
        loanId: r.loanId,
        roundId: r.invRoundId,
        SCHEMA: r.SCHEMA,
        garno: r.garno + 1,
        count: r.ADDRESS?.length ?? 0,
      });

      // จัดการ Style แต่ละ Cell
      excelRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.border = {
          top: { style: idxInGroup === 0 ? "medium" : "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });
  });

  // ── header style + protection ────────────────────────────────
  const header = ws.getRow(1);
  header.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.protection = { locked: LOCKED_COLS.has(colNumber) };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.font = { bold: true };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEFEFEF" },
    };
    cell.border = {
      top: { style: "medium" },
      left: { style: "thin" },
      bottom: { style: "medium" },
      right: { style: "thin" },
    };
  });
  header.height = 22;

  // ── Protect sheet (เรียกหลังสุดเสมอ) ────────────────────────
  await ws.protect("your_password", {
    selectLockedCells: true,
    selectUnlockedCells: true,
    formatCells: false,
    formatColumns: false,
    formatRows: false,
    insertRows: true, // ✅ 2. เปลี่ยนเป็น true เพื่ออนุญาตให้ Insert Row ได้
    deleteRows: false, // (ปรับเป็น true ได้ ถ้าอยากให้ลบ Row ได้ด้วย)
    sort: true,
    autoFilter: true,
  });

  // ── Export ───────────────────────────────────────────────────
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(
    blob,
    `รายการส่งสืบทรัพย์_${dayjs(data[0].dateRound).format("DD_MM_YY")}.xlsx`,
  );
}

// export async function exportExcelInvestigate(data = []) {
//   const workbook = new ExcelJS.Workbook();
//   const ws = workbook.addWorksheet("ข้อมูลลูกหนี้");

//   // หัวคอลัมน์ตามที่คุณใช้ก่อนหน้า
//   ws.columns = [
//     { header: "ลำดับ", key: "no", width: 8 },
//     { header: "เลขสัญญา", key: "contno", width: 18 },
//     { header: "รายชื่อ", key: "name", width: 24 },
//     { header: "รหัสบัตร", key: "cusCode", width: 20 },
//     { header: "ไม่พบ", key: "unFound", width: 12 },
//     { header: "พบ", key: "found", width: 12 },
//     { header: "หมายเหตุ", key: "remark", width: 45 },
//     { header: "คัด ทร.14", key: "grovermentCheckAmount", width: 16 },
//     { header: "ทะเบียนครอบครัว", key: "familyCheckAmount", width: 16 },
//     { header: "ดำเนินงาน", key: "operateAmount", width: 14 },
//     { header: "ตรวจเพิ่ม", key: "reCheckAmount", width: 14 },
//     { header: "รวม", key: "totalAmount", width: 12 },
//     { header: "customer_id", key: "cusId", width: 1 },
//     { header: "asset_online_id", key: "assetOnlineId", width: 1 },
//     { header: "inv_id", key: "invId", width: 1 },
//     { header: "loanId", key: "loanId", width: 1 },
//     { header: "roundId", key: "roundId", width: 1 },
//     { header: "SCHEMA", key: "SCHEMA", width: 1 },
//     { header: "garno", key: "garno", width: 1 },
//   ];

//   // ✅ ซ่อนคอลัมน์ M-P (index 13–19)
//   [13, 14, 15, 16, 17, 18, 19].forEach((col) => {
//     ws.getColumn(col).hidden = true;
//   });

//   // จัดกลุ่มตาม contno
//   const grouped = data.reduce((m, row) => {
//     const key = String(row?.contno || "").trim();
//     if (!key) return m;
//     if (!m.has(key)) m.set(key, []);
//     m.get(key).push(row);
//     return m;
//   }, new Map());

//   Array.from(grouped.entries()).forEach(([contno, rows], groupIndex) => {
//     rows.forEach((r, idxInGroup) => {
//       const fullName = [r.SNAM, r.NAME1, r.NAME2]
//         .filter(Boolean)
//         .join(" ")
//         .trim();

//       const excelRow = ws.addRow({
//         no: idxInGroup === 0 ? groupIndex + 1 : "", // ใช้ groupIndex แทน
//         contno: idxInGroup === 0 ? contno : "",
//         name: fullName || "",
//         cusCode: r.CUSCOD || "",
//         unFound: "",
//         found: "",
//         remark: "",
//         grovermentCheckAmount: "",
//         familyCheckAmount: "",
//         operateAmount: "",
//         reCheckAmount: "",
//         totalAmount: "",
//         cusId: r.customerId ?? "",
//         assetOnlineId: r.assetOnlineId ?? "",
//         invId: r.invId ?? "",
//         loanId: r.loanId,
//         roundId: r.invRoundId,
//         SCHEMA: r.SCHEMA,
//         garno: r.garno + 1,
//       });
//       excelRow.eachCell((cell) => {
//         cell.alignment = {
//           vertical: "middle",
//           horizontal: "center",
//           wrapText: true,
//         };
//         cell.border = {
//           top: { style: "thin" },
//           left: { style: "thin" },
//           bottom: { style: "thin" },
//           right: { style: "thin" },
//         };
//       });

//       // เส้นขอบหนาเฉพาะบรรทัดแรกของกลุ่ม
//       if (idxInGroup === 0) {
//         excelRow.eachCell((cell) => {
//           cell.border = {
//             top: { style: "medium" },
//             left: cell.border.left,
//             bottom: cell.border.bottom,
//             right: cell.border.right,
//           };
//         });
//       }
//     });
//   });

//   // ทำเส้นหัวตารางให้ดูชัด
//   const header = ws.getRow(1);
//   header.eachCell((cell) => {
//     cell.alignment = { vertical: "middle", horizontal: "center" };
//     cell.font = { bold: true };
//     cell.fill = {
//       type: "pattern",
//       pattern: "solid",
//       fgColor: { argb: "FFEFEFEF" },
//     };
//     cell.border = {
//       top: { style: "medium" },
//       left: { style: "thin" },
//       bottom: { style: "medium" },
//       right: { style: "thin" },
//     };
//   });
//   header.height = 22;

//   const buffer = await workbook.xlsx.writeBuffer();
//   const blob = new Blob([buffer], {
//     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
//   });
//   saveAs(
//     blob,
//     `รายการส่งสืบทรัพย์_${dayjs(data[0].dateRound).format("DD_MM_YY")}.xlsx`,
//   );
// }
